import React, { useContext, useEffect, useRef, useState } from "react";
import * as tf from "@tensorflow/tfjs";
import * as faceLandmarksDetection from "@tensorflow-models/face-landmarks-detection";
import { FACEMESH_TESSELATION } from "@mediapipe/face_mesh/face_mesh";

import axios from "axios";

import {
  PoseLandmarker,
  FilesetResolver,
  DrawingUtils,
} from "@mediapipe/tasks-vision";
import { AuthContext } from "../../../../context/App-Context";

const TgUserInput = () => {
  const auth = useContext(AuthContext);
  const codeTextAreaRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const mainCanvasRef = useRef(null);
  const imageRef = useRef(null);

  const [isCodeInput, setIsCodeInput] = useState(false);
  const [attachedImage, setAttachedImage] = useState(null);
  const [faceMeshes, setFaceMeshes] = useState([]);
  const [detector, setDetector] = useState(null);
  const [loadingModel, setLoadingModel] = useState(true);

  useEffect(() => {
    const loadModel = async () => {
      try {
        await tf.ready();
        const model = faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
        const detectorConfig = {
          runtime: "tfjs",
          refineLandmarks: true,
          maxFaces: 10,
        };
        const loadedDetector = await faceLandmarksDetection.createDetector(
          model,
          detectorConfig
        );
        setDetector(loadedDetector);
        setLoadingModel(false);
      } catch (err) {
        console.error("Failed to load FaceMesh:", err);
        setLoadingModel(false);
      }
    };
    loadModel();
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    const textArea = codeTextAreaRef.current;
    if (!textArea) return;

    const adjustHeight = () => {
      textArea.style.height = "auto";
      textArea.style.height = `${textArea.scrollHeight}px`;
      const lineCount = textArea.value ? textArea.value.split("\n").length : 1;
      const displayLines = Math.max(lineCount, 4);
      const lineNumbers = Array.from(
        { length: displayLines },
        (_, i) => i + 1
      ).join("\n");
      textArea.previousSibling.textContent = lineNumbers;
    };

    textArea.addEventListener("input", adjustHeight);
    textArea.addEventListener("keydown", (e) => {
      if (e.key === "Enter") setTimeout(adjustHeight, 0);
    });
    adjustHeight();

    return () => textArea.removeEventListener("input", adjustHeight);
  }, [isCodeInput]);

  ///////////////

  const handleImageLoad = (e) => {
    const imageElement = e.target;
    if (imageElement.complete) {
      processImage(imageElement);
    } else {
      imageElement.onload = () => {
        processImage(imageElement);
      };
    }
  };

  const processImage = async (imageElement) => {
    if (poseLandmarker && detector) {
      // Get face mesh predictions
      const predictions = await detector.estimateFaces(imageElement, {
        flipHorizontal: false,
      });

      // Get pose detection result
      const result = poseLandmarker.detect(imageElement);

      const canvas = mainCanvasRef.current;
      if (!canvas) return;

      // Match internal canvas resolution to the image's real size

      canvas.width = imageElement.naturalWidth;
      canvas.height = imageElement.naturalHeight;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(imageElement, 0, 0, canvas.width, canvas.height);
      // ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear previous drawings

      // Draw face mesh
      if (predictions.length > 0) {
        predictions.forEach((pred) => {
          const keypoints = pred.keypoints;

          ctx.strokeStyle = "#5100ff";
          ctx.lineWidth = 1;

          // Draw mesh connections
          FACEMESH_TESSELATION.forEach(([a, b]) => {
            const p1 = keypoints[a];
            const p2 = keypoints[b];

            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          });
        });
      }

      // Draw pose
      if (result.landmarks && result.landmarks.length > 0) {
        const drawingUtils = new DrawingUtils(ctx);
        const poseData = result.landmarks[0];
        const worldData = result.worldLandmarks[0];

        // Calculate Measurements
        const dist3D = (p1, p2) =>
          Math.sqrt(
            Math.pow(p1.x - p2.x, 2) +
              Math.pow(p1.y - p2.y, 2) +
              Math.pow(p1.z - p2.z, 2)
          );
        const shoulderCm = (dist3D(worldData[11], worldData[12]) * 100).toFixed(
          2
        );

        // Draw Wireframe
        drawingUtils.drawConnectors(poseData, POSE_CONNECTIONS, {
          color: "#5100ff",
          lineWidth: 4,
        });

        // Draw Measurement Text
        // ctx.fillStyle = "white";
        // ctx.strokeStyle = "black";
        // ctx.lineWidth = 6;
        // ctx.font = "bold 50px Arial";
        // ctx.strokeText(`Shoulder: ${shoulderCm} cm`, 50, 100);
        // ctx.fillText(`Shoulder: ${shoulderCm} cm`, 50, 100);
      }
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "pose-mesh.png";
        link.click();
        URL.revokeObjectURL(url); // Clean up memory
      }, "image/png");
    }
  };

  //////////////////////// process image

  const meshConnections = [
    // Face oval
    [10, 338],
    [338, 297],
    [297, 332],
    [332, 284],
    [284, 251],
    [251, 389],
    [389, 356],
    [356, 454],
    [454, 323],
    [323, 361],
    [361, 288],
    [288, 397],
    [397, 365],
    [365, 379],
    [379, 378],
    [378, 400],
    [400, 377],
    [377, 152],
    [152, 148],
    [148, 176],
    [176, 149],
    [149, 150],
    [150, 136],
    [136, 172],
    [172, 58],
    [58, 132],
    [132, 93],
    [93, 234],
    [234, 127],
    [127, 162],
    [162, 21],
    [21, 54],
    [54, 103],
    [103, 67],
    [67, 109],
    [109, 10],
    // Left eye
    [33, 7],
    [7, 163],
    [163, 144],
    [144, 145],
    [145, 153],
    [153, 154],
    [154, 155],
    [155, 133],
    [133, 33],
    // Right eye
    [263, 249],
    [249, 390],
    [390, 373],
    [373, 374],
    [374, 380],
    [380, 381],
    [381, 382],
    [382, 362],
    [362, 263],
    // Lips outer
    [61, 146],
    [146, 91],
    [91, 181],
    [181, 84],
    [84, 17],
    [17, 314],
    [314, 405],
    [405, 321],
    [321, 375],
    [375, 291],
    [291, 61],
    // Lips inner
    [78, 95],
    [95, 88],
    [88, 178],
    [178, 87],
    [87, 14],
    [14, 317],
    [317, 402],
    [402, 318],
    [318, 324],
    [324, 308],
    [308, 78],
  ];

  // Simple blue outline mesh (connect keypoints in order, just like your TestamentArk example)
  // Simple blue outline mesh (connect keypoints in order, just like your TestamentArk example)
  //   const drawFaceMesh = (predictions, imgElement) => {
  //     const canvas = mainCanvasRef.current;
  //     const ctx = canvas.getContext("2d");
  //     const displayWidth = imgElement.naturalWidth;
  //     const displayHeight = imgElement.naturalHeight;
  //     canvas.width = displayWidth;
  //     canvas.height = displayHeight;
  //     ctx.clearRect(0, 0, displayWidth, displayHeight);

  //     predictions.forEach((pred) => {
  //       const keypoints = pred.keypoints;

  //       ctx.strokeStyle = "#00bfff";
  //       ctx.lineWidth = 2;

  //       // Draw mesh connections

  //       console.log(FACEMESH_TESSELATION);
  //       meshConnections.forEach(([start, end]) => {
  //         const startPoint = keypoints[start];
  //         const endPoint = keypoints[end];
  //         ctx.beginPath();
  //         ctx.moveTo(startPoint.x, startPoint.y);
  //         ctx.lineTo(endPoint.x, endPoint.y);
  //         ctx.stroke();
  //       });
  //     });
  //   };

  /////// CURRENTLY WORKING ON

  const drawFaceMesh = (predictions, imgElement) => {
    const canvas = mainCanvasRef.current;
    const ctx = canvas.getContext("2d");
    const displayWidth = imgElement.naturalWidth;
    const displayHeight = imgElement.naturalHeight;
    canvas.width = displayWidth;
    canvas.height = displayHeight;
    ctx.clearRect(0, 0, displayWidth, displayHeight);

    predictions.forEach((pred) => {
      const keypoints = pred.keypoints;

      ctx.strokeStyle = "#00bfff";
      ctx.lineWidth = 1;

      // Draw mesh connections
      FACEMESH_TESSELATION.forEach(([a, b]) => {
        const p1 = keypoints[a];
        const p2 = keypoints[b];

        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      });
    });
  };

  ////////// saves image

  //   const saveImage = () => {
  //     const imageElement = imageRef.current;
  //     const canvas = mainCanvasRef.current;
  //     const newCanvas = document.createElement("canvas");
  //     newCanvas.width = canvas.width;
  //     newCanvas.height = canvas.height;
  //     const ctx = newCanvas.getContext("2d");

  //     // Draw the original image onto the new canvas
  //     ctx.drawImage(imageElement, 0, 0, newCanvas.width, newCanvas.height);

  //     // Draw the mesh onto the new canvas
  //     ctx.drawImage(canvas, 0, 0);

  //     // Get the data URL and trigger the download
  //     const url = newCanvas.toDataURL("image/png");
  //     const a = document.createElement("a");
  //     a.href = url;
  //     a.download = "image_with_mesh.png";
  //     a.click();
  //     URL.revokeObjectURL(url);
  //   };

  ////// image saved

  const createJsonFile = (data, filename) => {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  //////////// image saved as json

  //   const processImageForMesh = async (imageElement) => {
  //     console.log("processImageForMesh called");
  //     if (!detector || !imageElement) return;

  //     try {
  //       const predictions = await detector.estimateFaces(imageElement, {
  //         flipHorizontal: false,
  //       });

  //       //   if (predictions.length > 0) {
  //       //     createJsonFile(predictions, "face_mesh.json");

  //       //   }

  //       if (predictions.length > 0) {
  //         drawFaceMesh(predictions, imageElement);
  //         // saveImage(); ///////////////// saves image
  //       } else {
  //         const canvas = mainCanvasRef.current;
  //         if (canvas)
  //           canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
  //         setFaceMeshes([]);
  //       }
  //     } catch (err) {
  //       console.error("FaceMesh error:", err);
  //     }
  //   };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith("image/")) return;

    const previewUrl = URL.createObjectURL(file);
    setAttachedImage({ file, preview: previewUrl });
    setFaceMeshes([]);

    const img = new Image();
    img.onload = () => processImage(img);
    img.src = previewUrl;
  };

  //   const handleImageSelect = (e) => {
  //     const file = e.target.files[0];
  //     if (!file || !file.type.startsWith("image/")) return;

  //     const previewUrl = URL.createObjectURL(file);
  //     setAttachedImage({ file, preview: previewUrl });
  //     setFaceMeshes([]);

  //     const img = new Image();
  //     img.onload = () => processImageForMesh(img);
  //     img.src = previewUrl;
  //   };

  // Drag & drop (same as before)
  useEffect(() => {
    const dropZone = dropZoneRef.current;
    if (!dropZone) return;

    const handleDragOver = (e) => {
      e.preventDefault();
      dropZone.classList.add("drag-over");
    };
    const handleDragLeave = () => dropZone.classList.remove("drag-over");
    const handleDrop = (e) => {
      e.preventDefault();
      dropZone.classList.remove("drag-over");
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith("image/")) {
        const previewUrl = URL.createObjectURL(file);
        setAttachedImage({ file, preview: previewUrl });
        setFaceMeshes([]);

        const img = new Image();
        img.onload = () => processImage(img);
        img.src = previewUrl;
      }
    };

    dropZone.addEventListener("dragover", handleDragOver);
    dropZone.addEventListener("dragleave", handleDragLeave);
    dropZone.addEventListener("drop", handleDrop);

    return () => {
      dropZone.removeEventListener("dragover", handleDragOver);
      dropZone.removeEventListener("dragleave", handleDragLeave);
      dropZone.removeEventListener("drop", handleDrop);
    };
  }, [detector]);

  const removeImage = () => {
    if (attachedImage?.preview) URL.revokeObjectURL(attachedImage.preview);
    setAttachedImage(null);
    setFaceMeshes([]);

    const canvas = mainCanvasRef.current;
    if (canvas)
      canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
  };

  const [poseLandmarker, setPoseLandmarker] = useState(null);

  useEffect(() => {
    const createPoseLandmarker = async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm"
        );
        const landmarker = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: {
            // FIXED: Full versioned path to the Heavy model
            modelAssetPath: `https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_heavy/float16/1/pose_landmarker_heavy.task`,
            delegate: "CPU",
          },
          runningMode: "IMAGE",
          numPoses: 1,
          minPoseDetectionConfidence: 0.6, // Slightly higher for measurement accuracy
          minPosePresenceConfidence: 0.6,
        });
        setPoseLandmarker(landmarker);
      } catch (error) {
        console.error("Error creating pose landmarker:", error);
      }
    };
    createPoseLandmarker();
  }, []);

  //   const handleImageLoad = (e) => {
  //     const imageElement = e.target;
  //     if (imageElement.complete) {
  //       detectImageForMesh(imageElement);
  //     } else {
  //       imageElement.onload = () => {
  //         detectImageForMesh(imageElement);
  //       };
  //     }
  //   };
  const POSE_CONNECTIONS = [
    { start: 11, end: 12 },
    { start: 11, end: 13 },
    { start: 13, end: 15 }, // Left Arm
    { start: 12, end: 14 },
    { start: 14, end: 16 }, // Right Arm
    { start: 11, end: 23 },
    { start: 12, end: 24 },
    { start: 23, end: 24 }, // Torso
    { start: 23, end: 25 },
    { start: 25, end: 27 }, // Left Leg
    { start: 24, end: 26 },
    { start: 26, end: 28 }, // Right Leg
    { start: 27, end: 29 },
    { start: 28, end: 30 }, // Feet
    // left hand
    { start: 15, end: 13 },
    { start: 13, end: 11 },
    { start: 15, end: 17 },
    { start: 17, end: 19 },
    { start: 19, end: 21 },

    // Right Hand
    { start: 16, end: 14 },
    { start: 14, end: 12 },
    { start: 16, end: 18 },
    { start: 18, end: 20 },
    { start: 20, end: 22 },
  ];
  //   const detectImageForMesh = (imageElement) => {
  //     if (!poseLandmarker) {
  //       console.error("Landmarker not initialized yet");
  //       return;
  //     }

  //     // 1. Run the Synchronous Detection
  //     const result = poseLandmarker.detect(imageElement);

  //     if (result.landmarks && result.landmarks.length > 0) {
  //       const landmarks = result.landmarks[0]; // 2D points for drawing
  //       const worldLandmarks = result.worldLandmarks[0]; // 3D points for meters

  //       // 2. Calculate Shoulder Width (in cm)
  //       const dist3D = (p1, p2) =>
  //         Math.sqrt(
  //           Math.pow(p1.x - p2.x, 2) +
  //             Math.pow(p1.y - p2.y, 2) +
  //             Math.pow(p1.z - p2.z, 2)
  //         );
  //       const shoulderCm = (
  //         dist3D(worldLandmarks[11], worldLandmarks[12]) * 100
  //       ).toFixed(2);
  //       console.log(`Measured: ${shoulderCm} cm`);

  //       // 3. Setup Canvas to Create the Final Image
  //       const canvas = mainCanvasRef.current;
  //       canvas.width = imageElement.naturalWidth;
  //       canvas.height = imageElement.naturalHeight;
  //       const ctx = canvas.getContext("2d");

  //       // A. Draw the original background photo
  //       ctx.drawImage(imageElement, 0, 0, canvas.width, canvas.height);

  //       // B. Initialize DrawingUtils and Draw Skeleton
  //       const drawingUtils = new DrawingUtils(ctx);

  //       // Draw the Lines (Connectors)
  //       drawingUtils.drawConnectors(landmarks, POSE_CONNECTIONS, {
  //         color: "#00FF00", // Green wireframe
  //         lineWidth: 4,
  //       });

  //       // Draw the Joints (Landmarks)
  //       drawingUtils.drawLandmarks(landmarks, {
  //         color: "#FF0000", // Red dots
  //         lineWidth: 1,
  //         radius: 5,
  //       });

  //       // C. Draw the Measurement Text Overlay
  //       ctx.fillStyle = "white";
  //       ctx.strokeStyle = "black";
  //       ctx.lineWidth = 6;
  //       ctx.font = "bold 50px Arial";
  //       const label = `Shoulder: ${shoulderCm} cm`;
  //       ctx.strokeText(label, 50, 100);
  //       ctx.fillText(label, 50, 100);

  //       // 4. Export the final result

  //       ////
  //       const dataUrl = canvas.toDataURL("image/png");

  //       // Optional: Auto-download the result
  //       const link = document.createElement("a");
  //       link.download = `measurement_${Date.now()}.png`;
  //       link.href = dataUrl;
  //       link.click();

  //       // Or: Append to page to see it
  //       document.body.appendChild(canvas);
  //     } else {
  //       alert("No person detected in the image. Please try another photo.");
  //     }
  //   };

  const detectImageForMesh = (imageElement) => {
    if (!poseLandmarker) {
      console.error("Landmarker not initialized yet");
      return;
    }

    const result = poseLandmarker.detect(imageElement);

    if (result.landmarks && result.landmarks.length > 0) {
      const canvas = mainCanvasRef.current;
      if (!canvas) return;

      // Match internal canvas resolution to the image's real size
      canvas.width = imageElement.naturalWidth;
      canvas.height = imageElement.naturalHeight;

      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear previous drawings

      // IMPORTANT: If you want the photo visible UNDER the lines in the browser,
      // do NOT drawImage here (since the <img> tag is already showing it).
      // Only draw the lines on this canvas.

      const drawingUtils = new DrawingUtils(ctx);
      const poseData = result.landmarks[0];
      const worldData = result.worldLandmarks[0];

      // Calculate Measurements
      const dist3D = (p1, p2) =>
        Math.sqrt(
          Math.pow(p1.x - p2.x, 2) +
            Math.pow(p1.y - p2.y, 2) +
            Math.pow(p1.z - p2.z, 2)
        );
      const shoulderCm = (dist3D(worldData[11], worldData[12]) * 100).toFixed(
        2
      );

      // Draw Wireframe
      drawingUtils.drawConnectors(poseData, POSE_CONNECTIONS, {
        color: "#00FF00",
        lineWidth: 4,
      });
      //   drawingUtils.drawLandmarks(poseData, {
      //     color: "#FF0000",
      //     lineWidth: 1,
      //     radius: 5,
      //   });

      // Draw Measurement Text
      ctx.fillStyle = "white";
      ctx.strokeStyle = "black";
      ctx.lineWidth = 6;
      ctx.font = "bold 50px Arial";
      ctx.strokeText(`Shoulder: ${shoulderCm} cm`, 50, 100);
      ctx.fillText(`Shoulder: ${shoulderCm} cm`, 50, 100);
    } else {
      console.warn("No pose detected.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const textValue = isCodeInput
      ? codeTextAreaRef.current?.value || ""
      : document.querySelector(".input-field")?.value || "";

    alert(
      `Submitted! Text length: ${textValue.length}\nFaces detected: ${faceMeshes.length}`
    );
    // Your real submit logic here

    //console.log(textValue);

    // const postObjectforSubmital = {
    //   postText: textValue,
    //   postImages: ["imageIds"],
    //   postOcancerzZrhb: "ZrbID",
    //   uid: "",
    //   postDate: "dateOfPost",
    // };

    // console.log("hello username");
    // console.log(auth.loginVar);

    const postData = {
      userUsername: auth.loginVar || "",
      templeGridNormalPostData: {
        postText: textValue,
        postImages: [], // empty for now
        postOcancerzZrhb: "ZrbID",
        uid: "", // backend will generate
        postDate: new Date().toISOString(),
      },
    };

    // console.log(postObjectforSubmital);
    //console.log(auth.loginVar);

    // const formData = new FormData();

    // formData.append("userUsername", auth.loginVar); // 'profilePic' should match the field name your backend expects

    // formData.append(
    //   "templeGridNormalPostData",
    //   JSON.stringify(postObjectforSubmital)
    // ); // 'profilePic' should match the field name your backend expects

    // formData.append("profilePic", profilePic); // 'profilePic' should match the field name your backend expects

    try {
      //   // Replace with your actual backend endpoint
      //   const response = await axios.post(
      //     `${process.env.REACT_APP_BACKEND_URL}/zeusapolis/func-rts-upst/createtgridPost`,
      //     formData
      //     // {
      //     //   Authorization: `Bearer ${auth.token}`,
      //     //   // Do NOT set Content-Type — let Axios add the boundary
      //     // }
      //   );

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/zeusapolis/func-rts-upst/createtgridPost`,
        postData, // ← Sending as JSON, not FormData
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      ///console.log("Upload successful:", response);
      alert("Profile saved successfully!");

      // Optional: reset form or update UI with the new profile pic URL from response
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Error saving profile. Please try again.");
    }
  };

  /////////// post a post

  return (
    <div className="tg-user-input-wrapper">
      <div className="input-container">
        {/* Toggle */}
        <div className="toggle-container">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={isCodeInput}
              onChange={() => setIsCodeInput(!isCodeInput)}
            />
            <span className="toggle-slider"></span>
            {isCodeInput ? "Code Input" : "Text Input"}
          </label>
        </div>
        <input
          type="text"
          className={`input-field ${isCodeInput ? "hidden" : ""}`}
          placeholder="What's happening today?"
        />
        <div className={`code-container ${isCodeInput ? "" : "hidden"}`}>
          <pre className="line-numbers" aria-hidden="true">
            1\n2\n3\n4
          </pre>
          <textarea
            ref={codeTextAreaRef}
            className="code-input"
            placeholder="Enter code..."
            rows="4"
          />
        </div>
        {/* Image Upload Zone */}
        <div
          ref={dropZoneRef}
          className="image-upload-zone"
          onClick={() => fileInputRef.current?.click()}
        >
          {attachedImage ? (
            <div className="image-preview-container">
              <div className="image-preview-wrapper">
                <img
                  ref={imageRef}
                  src={attachedImage.preview}
                  alt="Uploaded"
                  className="preview-image"
                  onLoad={handleImageLoad}
                />
                <canvas ref={mainCanvasRef} className="face-overlay-canvas" />
                <div className="face-count-badge">
                  {loadingModel ? "Loading FaceMesh..." : `TempleGrid`}
                </div>
                <button
                  className="remove-image-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeImage();
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          ) : (
            <div className="upload-prompt">
              <div className="camera-icon">📷</div>
              <p>Click or drag an image here</p>
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          style={{ display: "none" }}
        />
        <button className="purple-button" onClick={handleSubmit}>
          Send
        </button>
      </div>
    </div>
  );
};

export default TgUserInput;
